/**
 * Load / generate / edit state for one LP's briefing.
 *
 * The API arrives injected rather than imported, for two reasons: the Sales
 * client page will adopt `BriefingView` with its own endpoints, and a test can
 * drive the whole lock-and-override lifecycle without a network. The host page
 * owns nothing but the wiring.
 */
import type { Briefing, SectionId } from './types';

export interface BriefingLock {
  holder_email: string | null;
  holder_name: string | null;
  expires_at: string | null;
  held_by_me: boolean;
}

/** Public research behind the briefing (backend `fund_research.research_status`). */
export interface BriefingResearch {
  status: 'none' | 'queued' | 'running' | 'ready' | 'partial' | 'failed' | 'capped' | 'needs_identity';
  error: string;
  started_at: string | null;
  finished_at: string | null;
  updated_at: string | null;
  public_sources: number;
  enabled: boolean;
  /** Per briefing question: needed facts held, searched without an answer, or never searched. */
  coverage?: ResearchCoverage[];
}

export interface ResearchCoverage {
  question: string;
  label: string;
  found: string[];
  not_found: string[];
  missing: string[];
}

export interface BriefingApi {
  get(counterpartyId: string): Promise<{
    briefing: Briefing | null;
    lock?: BriefingLock | null;
    generating?: boolean;
    /** Why the newest run failed; empty once a newer run succeeds. */
    error?: string;
    research?: BriefingResearch | null;
  }>;
  generate(counterpartyId: string): Promise<unknown>;
  /** Absent on hosts without public research. */
  refreshResearch?(counterpartyId: string, opts?: { confirmIdentity?: boolean }):
    Promise<{ research: BriefingResearch | null }>;
  putOverride(briefingId: string, sectionId: string, content: unknown): Promise<unknown>;
  deleteOverride(briefingId: string, sectionId: string): Promise<unknown>;
  lock(briefingId: string): Promise<BriefingLock>;
  heartbeat(briefingId: string): Promise<BriefingLock>;
  /** `keepalive` lets the request outlive a closing tab. */
  release(briefingId: string, opts?: { keepalive?: boolean }): Promise<unknown>;
}

export interface BriefingStoreOpts {
  /** How often to re-ask while a generation is running. */
  pollMs?: number;
  /** How often to re-ask while public research runs (minutes, not seconds). */
  researchPollMs?: number;
  /** Lock heartbeat. The server expires a lock after ~2 minutes idle, so this
   *  has to be comfortably shorter than that. */
  heartbeatMs?: number;
}

export function createBriefingStore(api: BriefingApi, opts: BriefingStoreOpts = {}) {
  const POLL = opts.pollMs ?? 2500;
  const RESEARCH_POLL = opts.researchPollMs ?? 30_000;
  const HEARTBEAT = opts.heartbeatMs ?? 30_000;

  let briefing = $state<Briefing | null>(null);
  let research = $state<BriefingResearch | null>(null);
  let researchError = $state('');
  let researchBusy = $state(false);
  let loading = $state(false);
  let generating = $state(false);
  let error = $state('');
  let saveState = $state<'idle' | 'saving' | 'saved' | 'error'>('idle');
  let lock = $state<BriefingLock | null>(null);

  let counterpartyId = '';
  // Every response carries the sequence it was requested under. Switching LP
  // bumps it, so a slow reply for the previous one is dropped instead of
  // overwriting the current briefing.
  let sequence = 0;
  let pollTimer: ReturnType<typeof setTimeout> | undefined;
  let beatTimer: ReturnType<typeof setInterval> | undefined;
  let savedTimer: ReturnType<typeof setTimeout> | undefined;
  // The briefing whose lock we hold; releasing must target it even after the
  // view has moved on to another LP's briefing.
  let heldId = '';
  // Whether the server granted us `heldId`. `lock` describes the briefing on
  // screen, which a load replaces, so it cannot answer this after a switch.
  let heldByMe = false;
  // Saves still on the wire. Releasing the lock before they land gets them a 409.
  const inflight = new Set<Promise<unknown>>();

  function stopPolling() {
    clearTimeout(pollTimer);
    pollTimer = undefined;
  }

  /** Lands every save already sent, then releases the lock it was taken on. */
  async function leaveEdit() {
    clearInterval(beatTimer);
    beatTimer = undefined;
    const id = heldId;
    if (!id) return;
    heldId = '';
    const mine = heldByMe;
    heldByMe = false;
    if (briefing?.briefing_id === id) lock = null;
    await Promise.allSettled([...inflight]);
    if (mine) await api.release(id).catch(() => {});
  }

  async function fetchOnce(mine: number) {
    const data = await api.get(counterpartyId);
    if (mine !== sequence) return null;
    briefing = data.briefing;
    lock = data.lock ?? null;
    generating = !!data.generating;
    research = data.research ?? null;
    // A run fails after POST has already succeeded; without this the poll ends on
    // the empty state with no word of why.
    if (data.error && !generating) error = data.error;
    return data;
  }

  // Research lands minutes later as a new briefing version; keep asking slowly until then.
  const researching = () => research?.status === 'queued' || research?.status === 'running';
  const shouldPoll = () => generating || researching();

  function scheduleNextPoll(mine: number) {
    stopPolling();
    pollTimer = setTimeout(() => void poll(mine), generating ? POLL : RESEARCH_POLL);
  }

  async function poll(mine: number) {
    if (mine !== sequence) return;
    try {
      const data = await fetchOnce(mine);
      if (data && shouldPoll()) scheduleNextPoll(mine);
    } catch {
      // A failed poll is not a failed briefing: keep what is on screen and try
      // again, so a blip does not blank a report somebody is reading. A stale
      // poll must not clear the timer of the LP now on screen.
      if (mine === sequence) scheduleNextPoll(mine);
    }
  }

  return {
    get briefing() { return briefing; },
    get research() { return research; },
    get researchError() { return researchError; },
    get researchBusy() { return researchBusy; },
    get loading() { return loading; },
    get generating() { return generating; },
    get error() { return error; },
    get saveState() { return saveState; },
    get lock() { return lock; },
    /** A teammate's name while they hold the lock, else ''. */
    get lockedBy() {
      return lock && !lock.held_by_me && lock.holder_email
        ? (lock.holder_name || lock.holder_email)
        : '';
    },

    async load(id: string) {
      if (!id) return;
      counterpartyId = id;
      const mine = ++sequence;
      stopPolling();
      briefing = null;
      research = null;
      researchError = '';
      researchBusy = false;
      lock = null;
      error = '';
      loading = true;
      try {
        const data = await fetchOnce(mine);
        if (data && shouldPoll()) scheduleNextPoll(mine);
      } catch (err) {
        if (mine !== sequence) return;
        // 404 is an answer, not a failure: this LP has no briefing yet, which the
        // view already has a first-class empty state for. Showing it as an error
        // would put a red line above the Generate button on every new LP — and
        // it is what the route returns until the generator ships.
        const status = (err as { status?: number })?.status;
        if (status === 404) briefing = null;
        else if (status === 403) error = 'This briefing is not available in this view.';
        else error = (err as Error)?.message || 'Could not load the briefing.';
      } finally {
        if (mine === sequence) loading = false;
      }
    },

    async generate() {
      if (!counterpartyId || generating) return;
      const mine = sequence;
      generating = true;
      error = '';
      try {
        await api.generate(counterpartyId);
        if (mine === sequence) scheduleNextPoll(mine);
      } catch (err) {
        if (mine !== sequence) return;
        generating = false;
        error = (err as Error)?.message || 'Could not start the briefing.';
      }
    },

    /** Start a deep research run; `confirmIdentity` answers a run stopped on an ambiguous name. */
    async refreshResearch(confirmIdentity = false) {
      if (!counterpartyId || !api.refreshResearch || researchBusy) return;
      const mine = sequence;
      researchBusy = true;
      researchError = '';
      try {
        const data = await api.refreshResearch(counterpartyId, { confirmIdentity });
        if (mine !== sequence) return;
        research = data.research ?? research;
        if (shouldPoll()) scheduleNextPoll(mine);
      } catch (err) {
        if (mine === sequence) researchError = (err as Error)?.message || 'Could not start public research.';
      } finally {
        if (mine === sequence) researchBusy = false;
      }
    },

    /** Persist one section override against the briefing it was typed on. The
     *  view has already applied it locally, so a failure shows as "Not saved"
     *  rather than reverting what was typed. */
    async save(sectionId: SectionId | 'summary', content: unknown, briefingId?: string) {
      const id = briefingId || briefing?.briefing_id;
      if (!id) return;
      const mine = sequence;
      saveState = 'saving';
      clearTimeout(savedTimer);
      const request = api.putOverride(id, sectionId, content);
      inflight.add(request);
      try {
        await request;
        if (mine !== sequence) return;
        saveState = 'saved';
        error = '';
        savedTimer = setTimeout(() => { if (saveState === 'saved') saveState = 'idle'; }, 2500);
      } catch (err) {
        if (mine !== sequence) return;
        saveState = 'error';
        const message = (err as Error)?.message || 'That edit was not saved.';
        // The server only edits the newest version; show that version instead of
        // leaving the page on one that can no longer be saved.
        if ((err as { status?: number })?.status === 409 && /newer briefing/i.test(message)) {
          error = 'A newer briefing was generated, so that edit was not saved. Edit the new version below.';
          void fetchOnce(mine).catch(() => {});
        } else {
          error = message;
        }
      } finally {
        inflight.delete(request);
      }
    },

    async revert(sectionId: SectionId | 'summary') {
      if (!briefing) return;
      const id = briefing.briefing_id;
      try {
        await api.deleteOverride(id, sectionId);
        await fetchOnce(sequence);
      } catch (err) {
        error = (err as Error)?.message || 'Could not revert that section.';
      }
    },

    /** Entering the doc view takes the edit lock and keeps it alive. */
    async enterEdit() {
      if (!briefing) return;
      const id = briefing.briefing_id;
      if (heldId === id) return;
      if (heldId) await leaveEdit();
      heldId = id;
      try {
        const taken = await api.lock(id);
        if (heldId === id) { lock = taken; heldByMe = taken.held_by_me; }
        // The view left edit mode while the lock was on its way back.
        else if (taken.held_by_me) void api.release(id).catch(() => {});
      } catch (err) {
        // A refused lock is the expected outcome when a teammate holds it; the
        // view falls back to a read-only doc.
        if (heldId === id) error = (err as Error)?.message || '';
      }
      if (heldId !== id) return;
      clearInterval(beatTimer);
      beatTimer = setInterval(() => {
        if (!heldByMe) return;
        api.heartbeat(id).then(next => {
          if (heldId === id) { lock = next; heldByMe = next.held_by_me; }
        }).catch(() => {});
      }, HEARTBEAT);
    },

    leaveEdit,

    /** The page is going away: no time to wait for saves, so only free the lock. */
    releaseOnUnload() {
      clearInterval(beatTimer);
      beatTimer = undefined;
      if (heldId && heldByMe) void api.release(heldId, { keepalive: true }).catch(() => {});
      heldId = '';
      heldByMe = false;
      lock = null;
    },

    /** Call from the host's teardown: timers outlive a component otherwise. */
    destroy() {
      sequence++;
      stopPolling();
      clearTimeout(savedTimer);
      void leaveEdit();
    },
  };
}
